import Button from '@components/Button';
import CtaLayout from '@components/CtaLayout';
import GiftTypeNotes from '@components/gift/GiftTypeNotes';
import Header from '@components/Header';
import Loading from '@components/Loading';
import TermNotes from '@components/term/TermNotes';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import useGiftBox from '@hooks/gift/useGiftBox';
import Receipt from '@icons/Receipt';
import { GiftBoxType, GiftCouponStatus, GiftItemType } from '@models/gift';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useTranslation } from 'react-i18next';

import GiftCard from './components/GiftCard';
import GiftInfo from './components/GiftInfo';
import GiftMessage from './components/GiftMessage';

interface Params {
    giftBoxItemId: string;
    giftItemType: GiftItemType;
}

declare module '@stackflow/config' {
    interface Register {
        GiftReceivedActivity: Params;
    }
}

const GiftReceivedActivity: ActivityComponentType<'GiftReceivedActivity'> = ({ params }) => {
    const { giftBoxItemId, giftItemType } = params;
    const { t } = useTranslation('gift');
    const { push, replace } = useFlow();
    const giftBox = useGiftBox(giftBoxItemId, {
        giftItemType,
        giftBoxType: GiftBoxType.RECEIVED,
    });

    const coupon = giftBox?.giftCoupon;
    const canOrder = coupon?.status === GiftCouponStatus.SENT
        || coupon?.status === GiftCouponStatus.ISSUED;

    const handleOrder = () => {
        replace('ShopListActivity', { type: 'LIST', isOrder: true });
    };

    const handleDelete = () => {
        if (!coupon?.id) return;
        push('DeleteGiftModal', { giftCouponId: coupon.id });
    };

    const handleUsage = () => {
        if (!coupon?.id) return;
        push('GiftUsageActivity', { giftCouponId: coupon.id });
    };

    return (
        <AppScreen>
            <CtaLayout
                header={(
                    <Header
                        title={t('giftReceived.title')}
                        trailing={coupon?.id ? (
                            <HistoryButton type='button'
                                id='gift-usage-button'
                                aria-label={t('giftReceived.usage.title')}
                                onClick={handleUsage}
                                css={pressActionEffect}>
                                <Receipt size={28} color={colors.gray9} />
                            </HistoryButton>
                        ) : undefined}
                    />
                )}
                footer={giftBox && (canOrder ? (
                    <Button fullWidth onClick={handleOrder} id='order-with-gift-button' >
                        {t('giftReceived.goOrder')}
                    </Button>
                ) : (
                    <Button fullWidth onClick={handleDelete} id='delete-gift-button'>
                        {t('giftReceived.delete.button')}
                    </Button>
                ))}
                noFooterBorder>
                <Page>
                    {!giftBox && <Loading size='small' />}
                    {giftBox && (
                        <>
                            <Content>
                                <GiftCard giftBox={giftBox} />
                                <GiftMessage message={giftBox.message} />
                                <InfoCard>
                                    <GiftInfo giftBox={giftBox} />
                                </InfoCard>
                            </Content>
                            <GiftTypeNotes giftItemType={giftBox.giftItemType} />
                            {/* <TermNotes displayContext='GIFT' noMargin noPadding variant='body' /> */}
                        </>
                    )}
                </Page>
            </CtaLayout>
        </AppScreen>
    );
};

const Page = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    background: ${colors.gray1};
    flex: 1;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);
    padding: var(--spacing-5) var(--spacing-4);
`;

const InfoCard = styled.div`
    width: 100%;
    padding: var(--spacing-5) var(--spacing-4);
    border-radius: 8px;
    background: ${colors.white};
`;

const HistoryButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
`;

export default GiftReceivedActivity;
